import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi, scheduleApi, contractApi, paymentApi } from '../api';
import {
  CUSTOMER_STATUSES, CUSTOMER_STATUS_COLORS,
  SCHEDULE_CATEGORIES, SCHEDULE_CATEGORY_COLORS, SCHEDULE_STATUS_COLORS,
  SCHEDULE_SECTIONS, SCHEDULE_STATUSES,
  PAYMENT_TYPE_COLORS, PAYMENT_STATUS_COLORS, PAYMENT_TYPES, PAYMENT_STATUSES, PAYMENT_METHODS,
  CONTRACT_TYPES, CONTRACT_STATUSES, ITEM_CATEGORIES,
  REFERRAL_SOURCES, REFERRAL_SOURCE_DETAILS,
  CUSTOMER_TYPES,
  SCHEDULE_TYPE_OPTIONS,
} from '../constants';

type TabType = 'OVERVIEW' | 'CONTRACTS' | 'SCHEDULES' | 'PAYMENTS' | 'MEMOS';

const TABS: { id: TabType; label: string; icon: string }[] = [
  { id: 'OVERVIEW', label: '기본정보', icon: 'ri-user-line' },
  { id: 'CONTRACTS', label: '계약정보', icon: 'ri-file-text-line' },
  { id: 'SCHEDULES', label: '일정정보', icon: 'ri-calendar-line' },
  { id: 'PAYMENTS', label: '결제정보', icon: 'ri-money-dollar-circle-line' },
  { id: 'MEMOS', label: '상담메모', icon: 'ri-chat-3-line' },
];

export default function MakeupCustomerDetail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = Number(searchParams.get('id'));
  const initialTab = (searchParams.get('tab') as TabType) || 'OVERVIEW';

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const changeTab = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ id: String(customerId), tab });
  };
  const [customer, setCustomer] = useState<any>(null);
  const [tabData, setTabData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});

  // 메모 모달
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [editingMemo, setEditingMemo] = useState<any>(null);
  const [memoForm, setMemoForm] = useState<any>({});

  // 일정 모달 (여러 개 동시 등록)
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<any>({});
  const [scheduleItems, setScheduleItems] = useState<any[]>([]);

  // 계약 등록 모달
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractForm, setContractForm] = useState<any>({});
  const [contractItems, setContractItems] = useState<any[]>([]);
  // 계약 품목 (모든 계약 자동 로드)
  const [contractItemsMap, setContractItemsMap] = useState<Record<number, any[]>>({});
  // 고객 검색 (계약 모달용)
  const [contractCustomerQuery, setContractCustomerQuery] = useState('');
  const [contractCustomerStatusFilter, setContractCustomerStatusFilter] = useState('');
  const [contractCustomerResults, setContractCustomerResults] = useState<any[]>([]);
  const [contractSelectedCustomer, setContractSelectedCustomer] = useState<any>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // 결제 등록 모달
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState<any>({});
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const fetchData = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const data = await customerApi.detail(customerId, activeTab);
      setCustomer(data.customer);
      setTabData(data);
      if (activeTab === 'OVERVIEW') {
        setEditForm(data.customer || {});
      }
      // 결제정보 탭에서 계약 데이터도 함께 로드 (요약 표시용)
      if (activeTab === 'PAYMENTS') {
        customerApi.detail(customerId, 'CONTRACTS').then((cData: any) => {
          setTabData((prev: any) => ({ ...prev, contracts: cData.contracts }));
        }).catch(() => {});
      }
    } catch (err) {
      console.error('고객 상세 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdate = async () => {
    try {
      const { makeupCustomerIdx, ownerVendor, assignedStaff, deleteYn, createdDt, updatedDt, partnerVendorNames, ...customerData } = editForm;
      await customerApi.update(customerId, customerData);
      alert('수정되었습니다.');
      setIsEditing(false);
      fetchData();
    } catch (err: any) {
      alert(`수정 실패: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!confirm('정말 이 고객을 삭제하시겠습니까?')) return;
    try {
      await customerApi.delete(customerId);
      alert('삭제되었습니다.');
      navigate('/makeup-shop/customers');
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // --- 메모 핸들러 ---
  const openMemoModal = (memo?: any) => {
    if (memo) {
      setEditingMemo(memo);
      setMemoForm({ ...memo });
    } else {
      setEditingMemo(null);
      setMemoForm({ consultationDt: new Date().toISOString().slice(0, 16) });
    }
    setShowMemoModal(true);
  };

  const handleSaveMemo = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingMemo) {
        await customerApi.memoUpdate(editingMemo.makeupConsultationMemoIdx, memoForm);
        alert('메모가 수정되었습니다.');
      } else {
        await customerApi.memoCreate(customerId, memoForm);
        alert('메모가 등록되었습니다.');
      }
      setShowMemoModal(false);
      fetchData();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDeleteMemo = async (id: number) => {
    if (!confirm('이 메모를 삭제하시겠습니까?')) return;
    try {
      await customerApi.memoDelete(id);
      alert('삭제되었습니다.');
      fetchData();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // --- 일정 핸들러 ---
  const emptyScheduleItem = () => ({
    scheduleCategory: 'CONSULTATION',
    title: '',
    startDt: '',
    endDt: '',
    memo: '',
  });

  const openScheduleModal = () => {
    setScheduleItems([emptyScheduleItem()]);
    setShowScheduleModal(true);
  };

  const handleScheduleItemChange = (index: number, field: string, value: string) => {
    setScheduleItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addScheduleItem = () => {
    setScheduleItems(prev => [...prev, emptyScheduleItem()]);
  };

  const removeScheduleItem = (index: number) => {
    if (scheduleItems.length === 1) return;
    setScheduleItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = scheduleItems.filter(item => item.title && item.startDt);
    if (validItems.length === 0) {
      alert('제목과 시작일시는 필수입니다.');
      return;
    }
    try {
      await Promise.all(validItems.map(item =>
        scheduleApi.create({
          section: 'SHOP',
          ...item,
          endDt: item.endDt || item.startDt,
          makeupCustomerIdx: customerId,
          customerName: customer?.groomName ? `${customer.groomName}/${customer.brideName}` : '',
          customerPhone: customer?.groomPhone || '',
        })
      ));
      alert(`${validItems.length}건의 일정이 등록되었습니다.`);
      setShowScheduleModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 계약 품목 자동 로드 ---
  useEffect(() => {
    if (activeTab === 'CONTRACTS' && tabData?.contracts?.length > 0) {
      const loadAllItems = async () => {
        const map: Record<number, any[]> = {};
        await Promise.all((tabData.contracts || []).map(async (c: any) => {
          const idx = c.makeupContractIdx || c.makeup_contract_idx;
          if (!idx) return;
          try {
            const data = await contractApi.detail(idx);
            map[idx] = data.items || [];
          } catch { map[idx] = []; }
        }));
        setContractItemsMap(map);
      };
      loadAllItems();
    }
  }, [activeTab, tabData?.contracts]);

  // --- 계약 핸들러 ---
  const openContractModal = () => {
    // 현재 보고 있는 고객을 기본 선택
    if (customer) {
      setContractSelectedCustomer({
        makeupCustomerIdx: customerId,
        groomName: customer.groomName,
        brideName: customer.brideName,
        groomPhone: customer.groomPhone,
      });
      setContractCustomerQuery(customer.groomName || customer.brideName || '');
    } else {
      setContractSelectedCustomer(null);
      setContractCustomerQuery('');
    }
    setContractForm({
      contractType: 'REHEARSAL_AND_MAIN',
      contractDt: new Date().toISOString().split('T')[0],
      discountAmount: 0,
      orderChannel: 'DIRECT',
      partnerVendorName: '',
      memo: '',
    });
    setContractItems([]);
    setShowCustomerDropdown(false);
    setContractCustomerStatusFilter('');
    setShowContractModal(true);
  };

  // 고객 검색
  useEffect(() => {
    if (!showContractModal) {
      setContractCustomerResults([]);
      return;
    }
    if (!contractCustomerQuery && !contractCustomerStatusFilter) {
      setContractCustomerResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await customerApi.list({
          q: contractCustomerQuery || undefined,
          status: contractCustomerStatusFilter || undefined,
          size: 20,
        });
        setContractCustomerResults(data.items || []);
        setShowCustomerDropdown(true);
      } catch {
        setContractCustomerResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contractCustomerQuery, contractCustomerStatusFilter, showContractModal]);

  const selectContractCustomer = (c: any) => {
    setContractSelectedCustomer(c);
    setContractCustomerQuery(c.groomName || c.brideName || '');
    setShowCustomerDropdown(false);
  };

  const addContractItemManual = () => {
    setContractItems([...contractItems, {
      id: Date.now(),
      itemCategory: 'REHEARSAL_MAKEUP',
      productName: '',
      quantity: 1,
      unitPrice: 0,
      memo: '',
    }]);
  };

  const removeContractItem = (id: number) => {
    setContractItems(contractItems.filter(item => item.id !== id));
  };

  const updateContractItem = (id: number, field: string, value: any) => {
    setContractItems(contractItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractSelectedCustomer) {
      alert('고객을 선택해주세요.');
      return;
    }
    if (contractItems.length === 0) {
      alert('품목을 하나 이상 추가해주세요.');
      return;
    }
    if (contractItems.some(item => !item.productName || !item.unitPrice)) {
      alert('모든 품목의 상품명과 단가는 필수입니다.');
      return;
    }
    try {
      const totalAmount = contractItems.reduce((sum: number, item: any) => sum + (item.unitPrice * item.quantity), 0);
      const { orderChannel, ...contractData } = contractForm;
      await contractApi.create({
        ...contractData,
        makeupCustomerIdx: contractSelectedCustomer.makeupCustomerIdx,
        totalAmount,
        items: contractItems.map(({ id, ...item }) => item),
      });

      // 계약일을 일정에 자동 등록
      const custName = contractSelectedCustomer.groomName ? `${contractSelectedCustomer.groomName}/${contractSelectedCustomer.brideName}` : '';
      const schedulePromises: Promise<any>[] = [];
      if (contractForm.contractDt) {
        schedulePromises.push(scheduleApi.create({
          section: 'SHOP', scheduleCategory: 'CONSULTATION',
          title: `${custName} 계약`,
          startDt: contractForm.contractDt,
          makeupCustomerIdx: contractSelectedCustomer.makeupCustomerIdx,
          customerName: custName,
        }).catch(() => {}));
      }
      await Promise.all(schedulePromises);

      alert('계약이 등록되었습니다.');
      setShowContractModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 결제 핸들러 ---
  const openPaymentModal = () => {
    setPaymentForm({
      makeupCustomerIdx: customerId,
      makeupContractIdx: '',
      items: [{ paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD', memo: '' }],
    });
    // 계약 목록 + 품목 로드
    customerApi.detail(customerId, 'CONTRACTS').then(async (data: any) => {
      const contracts = data.contracts || [];
      setTabData((prev: any) => ({ ...prev, contracts }));
      const map: Record<number, any[]> = {};
      await Promise.all(contracts.map(async (c: any) => {
        const idx = c.makeupContractIdx || c.makeup_contract_idx;
        if (!idx) return;
        try {
          const detail = await contractApi.detail(idx);
          map[idx] = detail.items || [];
        } catch { map[idx] = []; }
      }));
      setContractItemsMap(prev => ({ ...prev, ...map }));
    }).catch(() => {});
    setShowPaymentModal(true);
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentForm.makeupContractIdx) {
      alert('계약을 선택해주세요.');
      return;
    }
    const items = paymentForm.items || [paymentForm];
    const validItems = items.filter((i: any) => i.amount > 0 && i.paidDt);
    if (validItems.length === 0) {
      alert('결제 금액과 결제일을 입력해주세요.');
      return;
    }
    try {
      await Promise.all(validItems.map((item: any) =>
        paymentApi.create({
          makeupCustomerIdx: customerId,
          makeupContractIdx: Number(paymentForm.makeupContractIdx),
          ...item,
          amount: Number(item.amount),
        })
      ));
      // 예정 상태인 결제는 일정에 자동 등록
      const scheduledItems = validItems.filter((i: any) => i.status === 'SCHEDULED');
      if (scheduledItems.length > 0 && customer) {
        const custName = customer.groomName ? `${customer.groomName}/${customer.brideName}` : '';
        const getTypeLabel = (t: string) => ({ FULL: '일시납부', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '결제' }[t] || '결제');
        await Promise.all(scheduledItems.map((i: any) =>
          scheduleApi.create({
            section: 'SHOP',
            scheduleCategory: 'PAYMENT',
            title: `${custName} ${getTypeLabel(i.paymentType)} ${Number(i.amount).toLocaleString()}원`,
            startDt: i.paidDt,
            makeupCustomerIdx: customerId,
            customerName: custName,
          }).catch(() => {})
        ));
      }

      alert(`${validItems.length}건의 결제가 등록되었습니다.`);
      setShowPaymentModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/makeup-shop/customers')}
              className="text-gray-600 hover:text-gray-800 w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">고객 상세정보</h1>
              <p className="text-sm text-gray-600 mt-1">메이크업샵 고객 상세 정보</p>
            </div>
          </div>
          {activeTab === 'OVERVIEW' && (
            <div className="flex items-center gap-3">
              {customer && (
                <select value={customer.status || ''} onChange={async (e) => {
                  try {
                    await customerApi.update(customerId, { status: e.target.value });
                    fetchData();
                  } catch (err: any) { alert(`상태 변경 실패: ${err.message}`); }
                }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
                  {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {isEditing ? (
                <>
                  <button onClick={() => setIsEditing(false)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium cursor-pointer">취소</button>
                  <button onClick={handleUpdate}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">저장</button>
                </>
              ) : (
                <>
                  <button onClick={() => setIsEditing(true)}
                    className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 cursor-pointer">
                    <i className="ri-edit-line"></i> 수정
                  </button>
                  <button onClick={handleDelete}
                    className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 flex items-center gap-2 cursor-pointer">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* 고객 요약 카드 */}
        {customer && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center">
                <i className="ri-brush-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-gray-800">
                    {customer.groomName && customer.brideName
                      ? `${customer.groomName} / ${customer.brideName}`
                      : customer.groomName || customer.brideName || '-'}
                  </h2>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${CUSTOMER_STATUS_COLORS[customer.status] || 'bg-gray-100 text-gray-700'}`}>
                    {customer.status}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  {customer.groomPhone && <span><i className="ri-phone-line mr-1"></i>신랑: {customer.groomPhone}</span>}
                  {customer.bridePhone && <span><i className="ri-phone-line mr-1"></i>신부: {customer.bridePhone}</span>}
                  {customer.weddingDt && <span><i className="ri-calendar-line mr-1"></i>본식: {new Date(customer.weddingDt).toLocaleDateString('ko-KR')}</span>}
                  {customer.rehearsalDt && <span><i className="ri-brush-line mr-1"></i>리허설: {new Date(customer.rehearsalDt).toLocaleDateString('ko-KR')}</span>}
                  {customer.assignedStaff && <span><i className="ri-user-line mr-1"></i>담당: {customer.assignedStaff.name}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 탭 메뉴 */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => { changeTab(tab.id); setTabData(null); }}
                className={`flex-1 px-4 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>{tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 탭 컨텐츠 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : (
          <>
            {/* 기본정보 탭 */}
            {activeTab === 'OVERVIEW' && customer && (
              <div className="space-y-6">
                {/* 기본 정보 */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-6">기본 정보</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { label: '신랑명', key: 'groomName', required: true },
                      { label: '신랑 연락처', key: 'groomPhone', required: true },
                      { label: '신부명', key: 'brideName', required: true },
                      { label: '신부 연락처', key: 'bridePhone' },
                      { label: '발주일', key: 'orderDt', type: 'datetime-local' },
                      { label: '리허설일', key: 'rehearsalDt', type: 'datetime-local' },
                      { label: '본식일', key: 'weddingDt', type: 'datetime-local' },
                    ].map(field => {
                      const isDateField = field.type === 'datetime-local';
                      const toLocalDt = (v: string) => v ? v.replace(/\.000Z$/, '').slice(0, 16) : '';
                      const displayDate = (v: string) => {
                        if (!v) return '-';
                        try { return new Date(v).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }); }
                        catch { return v; }
                      };
                      return (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                          {isEditing ? (
                            <input
                              type={field.type || 'text'}
                              value={isDateField ? toLocalDt(editForm[field.key] || '') : (editForm[field.key] || '')}
                              onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-900">{isDateField ? displayDate(customer[field.key]) : (customer[field.key] || '-')}</p>
                          )}
                        </div>
                      );
                    })}
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">유입경로</label>
                      {isEditing ? (
                        <select value={editForm.referralSource || ''}
                          onChange={(e) => setEditForm({ ...editForm, referralSource: e.target.value, referralSourceDetail: '' })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {REFERRAL_SOURCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      ) : (
                        <p className="text-gray-900">{customer.referralSource || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">유입경로 상세</label>
                      {isEditing ? (
                        (REFERRAL_SOURCE_DETAILS[editForm.referralSource] || []).length > 0 ? (
                          <select value={editForm.referralSourceDetail || ''}
                            onChange={(e) => setEditForm({ ...editForm, referralSourceDetail: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">선택</option>
                            {(REFERRAL_SOURCE_DETAILS[editForm.referralSource] || []).map((d: any) => (
                              <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                          </select>
                        ) : (
                          <input type="text" value={editForm.referralSourceDetail || ''}
                            onChange={(e) => setEditForm({ ...editForm, referralSourceDetail: e.target.value })}
                            placeholder={editForm.referralSource ? '직접 입력' : '유입경로를 먼저 선택하세요'}
                            disabled={!editForm.referralSource}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
                        )
                      ) : (
                        <p className="text-gray-900">{customer.referralSourceDetail || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">고객 구분</label>
                      {isEditing ? (
                        <select value={editForm.customerType || ''} onChange={(e) => setEditForm({ ...editForm, customerType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {CUSTOMER_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                        </select>
                      ) : (
                        <p className="text-gray-900">{CUSTOMER_TYPES.find(ct => ct.value === customer.customerType)?.label || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">샵/출장 구분</label>
                      {isEditing ? (
                        <select value={editForm.scheduleType || ''} onChange={(e) => setEditForm({ ...editForm, scheduleType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {SCHEDULE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : (
                        <p className="text-gray-900">{SCHEDULE_TYPE_OPTIONS.find(o => o.value === customer.scheduleType)?.label || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">드레스샵명</label>
                      {isEditing ? (
                        <input type="text" value={editForm.dressShopName || ''}
                          onChange={(e) => setEditForm({ ...editForm, dressShopName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      ) : (
                        <p className="text-gray-900">{customer.dressShopName || '-'}</p>
                      )}
                    </div>
                  </div>

                  {/* 혼주정보 */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-bold text-gray-700 mb-4">혼주 정보</h4>
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        { label: '신랑측 혼주명', key: 'groomParentName' },
                        { label: '신랑측 혼주 연락처', key: 'groomParentPhone' },
                        { label: '신부측 혼주명', key: 'brideParentName' },
                        { label: '신부측 혼주 연락처', key: 'brideParentPhone' },
                      ].map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                          {isEditing ? (
                            <input type="text" value={editForm[field.key] || ''}
                              onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          ) : (
                            <p className="text-gray-900">{customer[field.key] || '-'}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {(customer.memo || isEditing) && (
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-500 mb-1">메모</label>
                      {isEditing ? (
                        <textarea value={editForm.memo || ''} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                      ) : (
                        <p className="text-gray-900 whitespace-pre-wrap">{customer.memo || '-'}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* 담당 스태프 정보 */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <i className="ri-team-line text-blue-500"></i> 담당 스태프
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { label: '리허설 메이크업', key: 'rehearsalMakeupStaff' },
                      { label: '리허설 헤어', key: 'rehearsalHairStaff' },
                      { label: '본식 메이크업', key: 'mainMakeupStaff' },
                      { label: '본식 헤어', key: 'mainHairStaff' },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                        {isEditing ? (
                          <input type="text" value={editForm[field.key] || ''}
                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{typeof customer[field.key] === 'object' ? customer[field.key]?.name : customer[field.key] || '-'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 계약정보 탭 */}
            {activeTab === 'CONTRACTS' && (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">계약 목록</h3>
                  <button onClick={openContractModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 신규 계약
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약일</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">발주업체</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">품목</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상품명</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약상태</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(tabData?.contracts || []).length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">계약 내역이 없습니다.</td></tr>
                      ) : (tabData?.contracts || []).map((c: any) => {
                        const contractIdx = c.makeupContractIdx || c.makeup_contract_idx;
                        const contractNo = c.contractNo || c.contract_no || '-';
                        const contractType = c.contractType || c.contract_type || '';
                        const finalAmt = c.finalAmount || c.final_amount || 0;
                        const status = c.status || '';
                        const items = contractItemsMap[contractIdx] || [];
                        const contractTypeLabel = CONTRACT_TYPES.find(t => t.value === contractType)?.label || contractType;

                        return (
                          <React.Fragment key={contractIdx}>
                            {/* 계약 헤더 행 */}
                            <tr className="bg-gray-50/50 hover:bg-gray-100/50 cursor-pointer" onClick={() => navigate(`/makeup-shop/contract-detail?id=${contractIdx}`)}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => navigate(`/makeup-shop/contract-detail?id=${contractIdx}`)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{contractNo}</button>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${contractType === 'REHEARSAL_AND_MAIN' ? 'bg-purple-100 text-purple-700' : contractType === 'REHEARSAL' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{contractTypeLabel}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{(() => { const dt = c.contractDt || c.contract_dt; if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }); } catch { return dt; } })()}</td>
                              <td className="px-4 py-3 text-xs text-gray-600">{c.partnerVendorName || c.partner_vendor_name || '-'}</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{Number(finalAmt).toLocaleString()}원</td>
                              <td className="px-4 py-3">
                                <select value={status} onClick={(e) => e.stopPropagation()} onChange={async (e) => {
                                  try {
                                    await contractApi.update(contractIdx, { status: e.target.value });
                                    fetchData();
                                  } catch (err: any) { alert(`변경 실패: ${err.message}`); }
                                }} className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none ${status === 'ACTIVE' ? 'bg-green-100 text-green-700' : status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                                  {CONTRACT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                              </td>
                            </tr>
                            {/* 품목 행들 */}
                            {items.map((item: any, idx: number) => {
                              const itemCategory = item.itemCategory || '';
                              const productName = item.productName || '-';
                              const categoryLabel = ITEM_CATEGORIES.find((ic: any) => ic.value === itemCategory)?.label || itemCategory;
                              return (
                                <tr key={item.makeupContractItemIdx || idx} className="hover:bg-blue-50/30">
                                  <td className="px-4 py-2.5 pl-12"></td>
                                  <td className="px-4 py-2.5"></td>
                                  <td className="px-4 py-2.5"></td>
                                  <td className="px-4 py-2.5">
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{categoryLabel}</span>
                                  </td>
                                  <td className="px-4 py-2.5 text-sm text-gray-900">{productName}</td>
                                  <td className="px-4 py-2.5 text-sm text-gray-500 text-right">{Number(item.unitPrice || 0).toLocaleString()}원</td>
                                  <td className="px-4 py-2.5"></td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 일정정보 탭 */}
            {activeTab === 'SCHEDULES' && (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">일정 목록</h3>
                  <button onClick={openScheduleModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 일정 등록
                  </button>
                </div>
                <div className="divide-y divide-gray-200">
                  {(tabData?.schedules || []).length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                      <i className="ri-calendar-line text-4xl block mb-2"></i>
                      등록된 일정이 없습니다.
                      <button onClick={openScheduleModal} className="block mx-auto mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer">
                        + 첫 일정 등록하기
                      </button>
                    </div>
                  ) : (tabData?.schedules || []).map((s: any) => {
                    const startDate = s.startDt || s.start_dt;
                    const endDate = s.endDt || s.end_dt;
                    const formatScheduleDate = (dt: string) => {
                      if (!dt) return '';
                      try {
                        const d = new Date(dt);
                        if (isNaN(d.getTime())) return '';
                        return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
                      } catch { return ''; }
                    };
                    return (
                      <div key={s.makeupScheduleIdx || s.makeup_schedule_idx} className="p-4 flex items-center gap-4 hover:bg-gray-50 group">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${SCHEDULE_CATEGORY_COLORS[s.scheduleCategory || s.schedule_category]?.split(' ')[0] || 'bg-gray-400'}`}></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${SCHEDULE_CATEGORY_COLORS[s.scheduleCategory || s.schedule_category] || 'bg-gray-100 text-gray-700'}`}>
                              {SCHEDULE_CATEGORIES.find(c => c.value === (s.scheduleCategory || s.schedule_category))?.label || s.scheduleCategory || s.schedule_category}
                            </span>
                            <span className="text-sm font-medium text-gray-900">{s.title}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatScheduleDate(startDate)}{endDate && endDate !== startDate ? ` ~ ${formatScheduleDate(endDate)}` : ''}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${SCHEDULE_STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-700'}`}>{s.status}</span>
                        <button onClick={async () => {
                          if (!confirm('이 일정을 삭제하시겠습니까?')) return;
                          try {
                            await scheduleApi.delete(s.makeupScheduleIdx || s.makeup_schedule_idx);
                            fetchData();
                          } catch (err: any) { alert(`삭제 실패: ${err.message}`); }
                        }} className="text-gray-300 hover:text-red-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 결제정보 탭 */}
            {activeTab === 'PAYMENTS' && (() => {
              const payments = (tabData?.payments || []);
              const totalPaid = payments.reduce((sum: number, p: any) => sum + Number(p.amount || p.paid_amount || 0), 0);
              const contracts = tabData?.contracts || [];
              const totalContract = contracts.reduce((sum: number, c: any) => sum + Number(c.finalAmount || c.final_amount || 0), 0);
              const remaining = totalContract - totalPaid;
              const getPaymentType = (t: string) => ({ FULL: '일시납부', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '기타' }[t] || t || '-');
              // 계약번호 매핑
              const contractNoMap: Record<number, string> = {};
              contracts.forEach((c: any) => {
                const idx = c.makeupContractIdx || c.makeup_contract_idx;
                const no = c.contractNo || c.contract_no || '';
                if (idx) contractNoMap[idx] = no;
              });
              const getPaymentMethod = (m: string) => ({ CARD: '카드', TRANSFER: '계좌이체', CASH: '현금' }[m] || m || '-');
              const getPaymentStatus = (s: string) => ({ COMPLETED: '완료', SCHEDULED: '예정', REFUNDED: '환불', CANCELLED: '취소' }[s] || s || '-');
              const formatDt = (dt: string) => { if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }); } catch { return dt; } };

              return (
                <div className="space-y-4">
                  {/* 결제 요약 */}
                  {totalContract > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-5">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">계약 총액</div>
                          <div className="text-lg font-bold text-gray-900">{totalContract.toLocaleString()}원</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">결제 완료</div>
                          <div className="text-lg font-bold text-blue-600">{totalPaid.toLocaleString()}원</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">미납금</div>
                          <div className={`text-lg font-bold ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>{remaining.toLocaleString()}원</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-lg font-bold text-gray-800">결제 내역</h3>
                      <button onClick={openPaymentModal}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                        <i className="ri-add-line"></i> 결제 등록
                      </button>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제일</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제유형</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제수단</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {payments.length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">결제 내역이 없습니다.</td></tr>
                        ) : payments.map((p: any) => {
                          const paymentType = p.paymentType || p.payment_type || '';
                          const paymentMethod = p.paymentMethod || p.payment_method || '';
                          const paidDt = p.paidDt || p.paid_dt || '';
                          const status = p.status || '';
                          return (
                            <tr key={p.makeupPaymentIdx || p.makeup_payment_idx} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedPayment(p)}>
                              <td className="px-4 py-4 text-xs text-blue-600 font-medium">{contractNoMap[p.makeupContractIdx || p.makeup_contract_idx] || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-700">{formatDt(paidDt)}</td>
                              <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[paymentType] || 'bg-gray-100 text-gray-700'}`}>{getPaymentType(paymentType)}</span></td>
                              <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{Number(p.amount || 0).toLocaleString()}원</td>
                              <td className="px-4 py-4 text-sm text-gray-700">{getPaymentMethod(paymentMethod)}</td>
                              <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{getPaymentStatus(status)}</span></td>
                              <td className="px-4 py-4 text-sm text-gray-500">{p.memo || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* 상담메모 탭 */}
            {activeTab === 'MEMOS' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">상담 메모</h3>
                  <button onClick={() => openMemoModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 메모 등록
                  </button>
                </div>
                {(tabData?.memos || []).length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500">상담 메모가 없습니다.</div>
                ) : (tabData?.memos || []).map((memo: any) => (
                  <div key={memo.makeupConsultationMemoIdx} className="bg-white rounded-lg shadow-sm p-6 mb-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="text-sm text-gray-500">{memo.consultationDt}</div>
                        {memo.createdBy && <div className="text-xs text-gray-400">작성자: {memo.createdBy.name}</div>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openMemoModal(memo)} className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer">수정</button>
                        <button onClick={() => handleDeleteMemo(memo.makeupConsultationMemoIdx)} className="text-red-600 hover:text-red-800 text-sm cursor-pointer">삭제</button>
                      </div>
                    </div>
                    {memo.skinType && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-blue-500">피부 타입:</span> {memo.skinType}</p>
                    )}
                    {memo.skinConcerns && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-orange-500">피부 고민:</span> {memo.skinConcerns}</p>
                    )}
                    {memo.makeupPreferences && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-pink-500">메이크업 선호:</span> {memo.makeupPreferences}</p>
                    )}
                    {memo.hairPreferences && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-purple-500">헤어 선호:</span> {memo.hairPreferences}</p>
                    )}
                    {memo.allergyInfo && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-red-500">알레르기 정보:</span> {memo.allergyInfo}</p>
                    )}
                    {memo.budgetRange && <p className="text-sm mb-2"><span className="text-xs font-medium text-green-500">예산:</span> {memo.budgetRange}</p>}
                    {memo.content && <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg mt-3 whitespace-pre-wrap">{memo.content}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* 일정 등록 모달 (여러 개 동시 등록) */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-lg font-bold">일정 등록</h2>
                  <p className="text-xs text-gray-400 mt-0.5">여러 일정을 한번에 등록할 수 있습니다</p>
                </div>
                <button onClick={() => setShowScheduleModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateSchedule} className="p-6 space-y-3">
                {scheduleItems.map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-gray-500">일정 {idx + 1}</span>
                      {scheduleItems.length > 1 && (
                        <button type="button" onClick={() => removeScheduleItem(idx)} className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">카테고리 *</label>
                        <select value={item.scheduleCategory} onChange={(e) => handleScheduleItemChange(idx, 'scheduleCategory', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">제목 *</label>
                        <input type="text" value={item.title} onChange={(e) => handleScheduleItemChange(idx, 'title', e.target.value)}
                          placeholder={`${customer?.groomName || customer?.brideName || ''} ${SCHEDULE_CATEGORIES.find(c => c.value === item.scheduleCategory)?.label || ''}`}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">시작일시 *</label>
                        <input type="datetime-local" value={item.startDt} onChange={(e) => handleScheduleItemChange(idx, 'startDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">종료일시</label>
                        <input type="datetime-local" value={item.endDt} onChange={(e) => handleScheduleItemChange(idx, 'endDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                    <div className="mt-2">
                      <input type="text" value={item.memo} onChange={(e) => handleScheduleItemChange(idx, 'memo', e.target.value)}
                        placeholder="메모" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addScheduleItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors flex items-center justify-center gap-1">
                  <i className="ri-add-line"></i> 일정 추가
                </button>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowScheduleModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm">
                    {scheduleItems.length > 1 ? `${scheduleItems.length}건 등록` : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 메모 등록/수정 모달 */}
        {showMemoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">{editingMemo ? '메모 수정' : '메모 등록'}</h2>
                <button onClick={() => setShowMemoModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleSaveMemo} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상담일시</label>
                  <input type="datetime-local" value={memoForm.consultationDt || ''} onChange={(e) => setMemoForm({ ...memoForm, consultationDt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">피부 타입</label>
                  <input type="text" value={memoForm.skinType || ''} onChange={(e) => setMemoForm({ ...memoForm, skinType: e.target.value })}
                    placeholder="예: 건성, 지성, 복합성, 민감성"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">피부 고민</label>
                  <input type="text" value={memoForm.skinConcerns || ''} onChange={(e) => setMemoForm({ ...memoForm, skinConcerns: e.target.value })}
                    placeholder="예: 여드름, 잡티, 홍조, 모공"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메이크업 선호</label>
                    <input type="text" value={memoForm.makeupPreferences || ''} onChange={(e) => setMemoForm({ ...memoForm, makeupPreferences: e.target.value })}
                      placeholder="예: 내추럴, 글램, 청순, 세미 스모키"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">헤어 선호</label>
                    <input type="text" value={memoForm.hairPreferences || ''} onChange={(e) => setMemoForm({ ...memoForm, hairPreferences: e.target.value })}
                      placeholder="예: 업스타일, 반업, 다운스타일"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">알레르기 정보</label>
                    <input type="text" value={memoForm.allergyInfo || ''} onChange={(e) => setMemoForm({ ...memoForm, allergyInfo: e.target.value })}
                      placeholder="예: 라텍스, 특정 화장품 성분"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예산 범위</label>
                    <input type="text" value={memoForm.budgetRange || ''} onChange={(e) => setMemoForm({ ...memoForm, budgetRange: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">내용</label>
                  <textarea value={memoForm.content || ''} onChange={(e) => setMemoForm({ ...memoForm, content: e.target.value })} rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowMemoModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">{editingMemo ? '수정' : '등록'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 계약 등록 모달 */}
        {showContractModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold">계약 등록</h2>
                <button onClick={() => setShowContractModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateContract} className="p-6 space-y-6">
                {/* 고객 선택 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-user-line text-gray-400"></i> 고객 선택
                  </h3>
                  {contractSelectedCustomer ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600">
                        {(contractSelectedCustomer.groomName || contractSelectedCustomer.brideName || '?').charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {contractSelectedCustomer.groomName && contractSelectedCustomer.brideName
                            ? `${contractSelectedCustomer.groomName} / ${contractSelectedCustomer.brideName}`
                            : contractSelectedCustomer.groomName || contractSelectedCustomer.brideName}
                        </div>
                        <div className="text-xs text-gray-500">{contractSelectedCustomer.groomPhone}{contractSelectedCustomer.weddingDt ? ` | 본식: ${new Date(contractSelectedCustomer.weddingDt).toLocaleDateString('ko-KR')}` : ''}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${CUSTOMER_STATUS_COLORS[contractSelectedCustomer.status] || 'bg-gray-100 text-gray-700'}`}>{contractSelectedCustomer.status}</span>
                      <button type="button" onClick={() => { setContractSelectedCustomer(null); setContractCustomerQuery(''); setContractCustomerStatusFilter(''); }}
                        className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-lg"></i></button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* 상태 필터 */}
                      <div className="flex flex-wrap gap-1.5">
                        <button type="button" onClick={() => setContractCustomerStatusFilter('')}
                          className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                            contractCustomerStatusFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}>전체</button>
                        {CUSTOMER_STATUSES.map(s => (
                          <button key={s} type="button" onClick={() => setContractCustomerStatusFilter(contractCustomerStatusFilter === s ? '' : s)}
                            className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                              contractCustomerStatusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}>{s}</button>
                        ))}
                      </div>
                      {/* 검색 */}
                      <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input type="text" placeholder="고객명 또는 전화번호로 검색" value={contractCustomerQuery}
                          onChange={(e) => { setContractCustomerQuery(e.target.value); setShowCustomerDropdown(true); }}
                          onFocus={() => setShowCustomerDropdown(true)}
                          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      {/* 검색 결과 목록 */}
                      {showCustomerDropdown && contractCustomerResults.length > 0 && (
                        <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                          {contractCustomerResults.map((c: any) => (
                            <button key={c.makeupCustomerIdx} type="button"
                              onClick={() => selectContractCustomer(c)}
                              className="w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 cursor-pointer border-b border-gray-100 last:border-b-0">
                              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                                {(c.groomName || c.brideName || '?').charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">{c.groomName}{c.brideName ? ` / ${c.brideName}` : ''}</div>
                                <div className="text-xs text-gray-500">{c.groomPhone}{c.weddingDt ? ` | 본식: ${new Date(c.weddingDt).toLocaleDateString('ko-KR')}` : ''}</div>
                              </div>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${CUSTOMER_STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700'}`}>{c.status}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {showCustomerDropdown && (contractCustomerQuery || contractCustomerStatusFilter) && contractCustomerResults.length === 0 && (
                        <div className="text-center py-4 text-sm text-gray-400">검색 결과가 없습니다.</div>
                      )}
                    </div>
                  )}
                </div>

                {/* 계약 기본 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-file-text-line text-gray-400"></i> 계약 정보
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약 유형 *</label>
                      <select value={contractForm.contractType || 'REHEARSAL_AND_MAIN'} onChange={(e) => setContractForm({ ...contractForm, contractType: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약일 *</label>
                      <input type="date" value={contractForm.contractDt || ''} onChange={(e) => setContractForm({ ...contractForm, contractDt: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">할인금액</label>
                      <input type="number" value={contractForm.discountAmount || 0} onChange={(e) => setContractForm({ ...contractForm, discountAmount: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">접수 경로</label>
                      <select value={contractForm.orderChannel || 'DIRECT'} onChange={(e) => setContractForm({ ...contractForm, orderChannel: e.target.value, partnerVendorName: e.target.value === 'DIRECT' ? '' : contractForm.partnerVendorName })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="DIRECT">직접 방문</option>
                        <option value="VENDOR">업체 접수</option>
                      </select>
                    </div>
                    {contractForm.orderChannel === 'VENDOR' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">발주업체명</label>
                        <input type="text" value={contractForm.partnerVendorName || ''} onChange={(e) => setContractForm({ ...contractForm, partnerVendorName: e.target.value })}
                          placeholder="업체명 입력"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                    <textarea value={contractForm.memo || ''} onChange={(e) => setContractForm({ ...contractForm, memo: e.target.value })} rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  </div>
                </div>

                {/* 품목 */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <i className="ri-list-check text-gray-400"></i> 품목 목록
                    </h3>
                    <button type="button" onClick={addContractItemManual}
                      className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center gap-1 cursor-pointer">
                      <i className="ri-add-line"></i> 직접 입력
                    </button>
                  </div>

                  {contractItems.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                      <i className="ri-brush-line text-3xl text-gray-300 block mb-2"></i>
                      <p className="text-sm text-gray-400 mb-3">품목을 추가해주세요</p>
                      <button type="button" onClick={addContractItemManual}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-1 mx-auto">
                        <i className="ri-edit-line"></i> 직접 입력
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contractItems.map((item, idx) => (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-xs font-semibold text-gray-500">품목 {idx + 1}</span>
                            <button type="button" onClick={() => removeContractItem(item.id)}
                              className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">카테고리</label>
                              <select value={item.itemCategory} onChange={(e) => updateContractItem(item.id, 'itemCategory', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {ITEM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">상품명 *</label>
                              <input type="text" value={item.productName} onChange={(e) => updateContractItem(item.id, 'productName', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">수량</label>
                              <input type="number" min={1} value={item.quantity} onChange={(e) => updateContractItem(item.id, 'quantity', Number(e.target.value))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">단가 *</label>
                              <input type="number" value={item.unitPrice} onChange={(e) => updateContractItem(item.id, 'unitPrice', Number(e.target.value))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                          </div>
                          <div className="mt-2">
                            <label className="block text-xs text-gray-600 mb-1">비고</label>
                            <input type="text" value={item.memo || ''} onChange={(e) => updateContractItem(item.id, 'memo', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 합계 */}
                  {contractItems.length > 0 && (
                    <div className="mt-4 bg-blue-50 rounded-lg p-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">총 금액</span>
                        <span className="font-semibold text-gray-900">{contractItems.reduce((sum: number, item: any) => sum + (item.unitPrice * item.quantity), 0).toLocaleString()}원</span>
                      </div>
                      {(contractForm.discountAmount || 0) > 0 && (
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-gray-600">할인</span>
                          <span className="font-semibold text-red-600">-{(contractForm.discountAmount || 0).toLocaleString()}원</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm mt-1 pt-1 border-t border-blue-200">
                        <span className="font-semibold text-gray-800">최종 금액</span>
                        <span className="font-bold text-blue-700">
                          {(contractItems.reduce((sum: number, item: any) => sum + (item.unitPrice * item.quantity), 0) - (contractForm.discountAmount || 0)).toLocaleString()}원
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowContractModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 결제 상세 모달 */}
        {selectedPayment && (() => {
          const p = selectedPayment;
          const paymentType = p.paymentType || p.payment_type || '';
          const paymentMethod = p.paymentMethod || p.payment_method || '';
          const paidDt = p.paidDt || p.paid_dt || '';
          const status = p.status || '';
          const paymentIdx = p.makeupPaymentIdx || p.makeup_payment_idx;
          const getTypeLabel = (t: string) => ({ FULL: '일시납부', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '기타' }[t] || t);
          const getMethodLabel = (m: string) => ({ CARD: '카드', TRANSFER: '계좌이체', CASH: '현금' }[m] || m || '-');
          const getStatusLabel = (s: string) => ({ COMPLETED: '완료', SCHEDULED: '예정', REFUNDED: '환불', CANCELLED: '취소' }[s] || s);
          const fmtDt = (dt: string) => { if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return dt; } };
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-bold">결제 상세</h2>
                  <button onClick={() => setSelectedPayment(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><div className="text-xs text-gray-500 mb-1">계약</div><div className="text-sm font-medium">{(() => { const c = (tabData?.contracts || []).find((c: any) => (c.makeupContractIdx || c.makeup_contract_idx) === (p.makeupContractIdx || p.makeup_contract_idx)); return c?.contractNo || c?.contract_no || '-'; })()}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제일</div><div className="text-sm font-medium">{fmtDt(paidDt)}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제유형</div><div><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[paymentType] || 'bg-gray-100'}`}>{getTypeLabel(paymentType)}</span></div></div>
                    <div><div className="text-xs text-gray-500 mb-1">금액</div><div className="text-lg font-bold text-gray-900">{Number(p.amount || 0).toLocaleString()}원</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제수단</div><div className="text-sm">{getMethodLabel(paymentMethod)}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">상태</div><div><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100'}`}>{getStatusLabel(status)}</span></div></div>
                  </div>
                  {p.memo && <div><div className="text-xs text-gray-500 mb-1">메모</div><div className="text-sm text-gray-700">{p.memo}</div></div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                  <button onClick={async () => {
                    if (!confirm('이 결제를 삭제하시겠습니까?')) return;
                    try {
                      await paymentApi.delete(paymentIdx);
                      setSelectedPayment(null);
                      fetchData();
                    } catch (err: any) { alert(`삭제 실패: ${err.message}`); }
                  }} className="text-red-500 hover:text-red-700 text-sm cursor-pointer flex items-center gap-1">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                  <button onClick={() => setSelectedPayment(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 cursor-pointer">닫기</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* 결제 등록 모달 (여러 건 동시 등록) */}
        {showPaymentModal && (() => {
          const paymentItems = paymentForm.items || [{ paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD', memo: '' }];
          const updatePaymentItem = (idx: number, field: string, value: any) => {
            const newItems = [...paymentItems];
            newItems[idx] = { ...newItems[idx], [field]: value };
            setPaymentForm({ ...paymentForm, items: newItems });
          };
          const addPaymentItem = () => {
            setPaymentForm({ ...paymentForm, items: [...paymentItems, { paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'INTERMEDIATE', status: 'SCHEDULED', paymentMethod: 'CARD', memo: '' }] });
          };
          const removePaymentItem = (idx: number) => {
            if (paymentItems.length <= 1) return;
            setPaymentForm({ ...paymentForm, items: paymentItems.filter((_: any, i: number) => i !== idx) });
          };

          return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-lg font-bold">결제 등록</h2>
                  <p className="text-xs text-gray-400 mt-0.5">여러 결제를 한번에 등록할 수 있습니다</p>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreatePayment} className="p-6 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">계약 선택 *</label>
                  <select value={paymentForm.makeupContractIdx || ''} onChange={(e) => setPaymentForm({ ...paymentForm, makeupContractIdx: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">계약을 선택하세요</option>
                    {(tabData?.contracts || []).map((c: any) => {
                      const idx = c.makeupContractIdx || c.makeup_contract_idx;
                      const no = c.contractNo || c.contract_no || `계약 ${idx}`;
                      const amt = c.finalAmount || c.final_amount || 0;
                      const citems = contractItemsMap[idx] || [];
                      const itemNames = citems.map((i: any) => i.productName || i.product_name).filter(Boolean).join(', ');
                      return <option key={idx} value={idx}>{no} - {itemNames || '품목없음'} ({Number(amt).toLocaleString()}원)</option>;
                    })}
                  </select>
                </div>

                {paymentItems.map((pi: any, idx: number) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-gray-500">결제 {idx + 1}</span>
                      {paymentItems.length > 1 && (
                        <button type="button" onClick={() => removePaymentItem(idx)} className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제일 *</label>
                        <input type="date" value={pi.paidDt || ''} onChange={(e) => updatePaymentItem(idx, 'paidDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">금액 *</label>
                        <input type="number" value={pi.amount || ''} onChange={(e) => updatePaymentItem(idx, 'amount', Number(e.target.value))}
                          placeholder="0" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제유형</label>
                        <select value={pi.paymentType || 'CONTRACT'} onChange={(e) => updatePaymentItem(idx, 'paymentType', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">상태</label>
                        <select value={pi.status || 'COMPLETED'} onChange={(e) => updatePaymentItem(idx, 'status', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제수단</label>
                        <select value={pi.paymentMethod || 'CARD'} onChange={(e) => updatePaymentItem(idx, 'paymentMethod', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">메모</label>
                        <input type="text" value={pi.memo || ''} onChange={(e) => updatePaymentItem(idx, 'memo', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" onClick={addPaymentItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors flex items-center justify-center gap-1">
                  <i className="ri-add-line"></i> 결제 추가
                </button>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowPaymentModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
}
